/**
 * Widget Error State Component
 *
 * Feature: 136-dashboard-alignment
 * Description: Error state with retry button, error message, and warning icon.
 *   Used by all dashboard widgets when API calls fail.
 * Constitution: v1.12.0 — Principle XXXVII (Code Header Standards), XXIV (API Resilience)
 */

import React from 'react';
import clsx from 'clsx';

interface WidgetErrorStateProps {
  /** Error message to display */
  message?: string;
  /** Retry callback */
  onRetry?: () => void;
  /** Optional CSS class for the outer container */
  className?: string;
  /** Widget title shown above the error */
  title?: string;
}

/**
 * Error state component for dashboard widgets.
 * Displays a warning icon, error message, and an optional retry button.
 */
export const WidgetErrorState: React.FC<WidgetErrorStateProps> = ({
  message = 'Failed to load data',
  onRetry,
  className,
  title,
}) => (
  <div
    className={clsx(
      'bg-[var(--color-bg-card)] rounded-lg border border-[var(--color-border)] p-4',
      className
    )}
    role="alert"
  >
    {title && (
      <div className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider mb-4">
        {title}
      </div>
    )}

    <div className="flex flex-col items-center justify-center py-8 text-center">
      {/* Warning icon */}
      <svg
        className="w-10 h-10 text-red-400 dark:text-red-500 mb-3"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
        />
      </svg>

      <p className="text-sm text-[var(--color-text-secondary)] mb-4 max-w-xs">
        {message}
      </p>

      {onRetry && (
        <button
          onClick={onRetry}
          className="px-4 py-2 text-sm font-medium text-white bg-[var(--color-accent)] rounded-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-offset-2 focus:ring-offset-[var(--color-bg-card)] transition-colors"
        >
          Retry
        </button>
      )}
    </div>
  </div>
);

export default WidgetErrorState;
